import { useNavigate, useSearchParams } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * One timer for every mounted sidebar. The desktop rail and the mobile sheet
 * each call this hook, and each used to debounce on its own. The instance that
 * lost the race wrote an older `?q=` back into the box, so fast typing kept
 * the previous prefix and a click opened nothing.
 */
let searchSyncGeneration = 0;
let searchSyncTimer: number | null = null;
/** `?q=` value this hook just wrote. Every instance must ignore that echo. */
let ignoreUrlQuery: string | null = null;
/**
 * A result click already set `?policy=`. A search write that was already
 * handed to the router can still land afterwards and drop that id. Keep
 * putting it back until this window closes.
 */
let pendingPolicyId: string | null = null;
let pendingPolicyUntil = 0;

export function beginOpenPolicyFromSearch(policyId: string) {
  pendingPolicyId = policyId;
  pendingPolicyUntil = Date.now() + 1000;
  searchSyncGeneration += 1;
  if (searchSyncTimer != null) {
    window.clearTimeout(searchSyncTimer);
    searchSyncTimer = null;
  }
}

function policyOpenPending(): string | null {
  if (!pendingPolicyId) return null;
  if (Date.now() > pendingPolicyUntil) {
    pendingPolicyId = null;
    return null;
  }
  return pendingPolicyId;
}

function armPolicySearchSync(run: () => void) {
  if (searchSyncTimer != null) window.clearTimeout(searchSyncTimer);
  const generation = ++searchSyncGeneration;
  searchSyncTimer = window.setTimeout(() => {
    searchSyncTimer = null;
    if (generation !== searchSyncGeneration) return;
    run();
  }, 300);
}
import {
  ORG_TEAM_SELECTION_CHANGED_EVENT,
  useOrgTeam,
} from "#app/contexts/org-team-context.tsx";
import { usePolicyListStore } from "#app/store/policy-list";
import { fetchPolicyList } from "#app/utils/policy-list";

/**
 * Paginated policy list for the sidebar Recent slot and the policies page.
 * Backed by `usePolicyListStore` so both surfaces share one fetch.
 */
export function usePolicyListSidebar(options?: {
  /**
   * When true, the first list item is written into `?policy=` if none is set.
   * Default **false** — `/u/policies` shows the published library landing (P154).
   */
  autoSelectFirst?: boolean;
  enabled?: boolean;
  /** When false, read store + `load` only — sidebar owns URL/search sync. */
  syncEffects?: boolean;
}) {
  const enabled = options?.enabled !== false;
  const syncEffects = options?.syncEffects !== false;
  const autoSelectFirst = options?.autoSelectFirst === true;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentOrg, currentTeam } = useOrgTeam();
  // Memoised because two useCallbacks depend on it. Built inline it was a new
  // object on every render, so those callbacks were rebuilt every render too --
  // which is what the rule reports rather than a missing name.
  const svcContext = useMemo(
    () => ({
      orgName: currentOrg?.name,
      teamName: currentTeam?.name,
      teamKey: currentTeam?.prefix,
    }),
    [currentOrg?.name, currentTeam?.name, currentTeam?.prefix],
  );

  const rules = usePolicyListStore((s) => s.rules);
  const search = usePolicyListStore((s) => s.search);
  const setSearch = usePolicyListStore((s) => s.setSearch);
  const loading = usePolicyListStore((s) => s.loading);
  const listError = usePolicyListStore((s) => s.listError);
  const cursor = usePolicyListStore((s) => s.cursor);
  const loadingMore = usePolicyListStore((s) => s.loadingMore);
  const beginLoad = usePolicyListStore((s) => s.beginLoad);
  const setRules = usePolicyListStore((s) => s.setRules);
  const appendRules = usePolicyListStore((s) => s.appendRules);
  const setLoading = usePolicyListStore((s) => s.setLoading);
  const setListError = usePolicyListStore((s) => s.setListError);
  const setCursor = usePolicyListStore((s) => s.setCursor);
  const setLoadingMore = usePolicyListStore((s) => s.setLoadingMore);

  const firstLoad = useRef(true);
  // Keep URL selection on a ref so `load` is not recreated on every ?policy=
  // change (that race left loading=true forever across two hook instances).
  const selectedIdRef = useRef(searchParams.get("policy"));
  const latestParams = useRef(searchParams);
  selectedIdRef.current = searchParams.get("policy");
  useEffect(() => {
    latestParams.current = searchParams;
  }, [searchParams]);

  const patchSearchParams = useCallback(
    (mutate: (next: URLSearchParams) => void, replace = true) => {
      // Read the live address bar, not only the React snapshot. A debounced
      // search write armed before the user clicked a result can otherwise
      // rebuild from pre-click params and drop the `?policy=` navigation that
      // committed in between — search appears to do nothing on click.
      const live =
        typeof window !== "undefined"
          ? window.location.search
          : latestParams.current.toString();
      const next = new URLSearchParams(live);
      mutate(next);
      latestParams.current = next;
      navigate(`?${next}`, { replace, preventScrollReset: true });
    },
    [navigate],
  );

  const load = useCallback(
    (term?: string) => {
      if (!enabled) return;
      const seq = beginLoad();
      const query = term ?? usePolicyListStore.getState().search;
      const isCurrent = () =>
        seq === usePolicyListStore.getState().loadGeneration;
      void fetchPolicyList({ search: query, ctx: svcContext })
        .then((page) => {
          if (!isCurrent()) return;
          setRules(page.rules);
          setCursor(page.hasMore ? page.nextCursor : null);
          if (
            autoSelectFirst &&
            page.rules.length > 0 &&
            !selectedIdRef.current &&
            !latestParams.current.get("new")
          ) {
            patchSearchParams((next) => {
              next.set("policy", page.rules[0]!.publicId);
            });
          }
        })
        .catch((error) => {
          if (isCurrent()) {
            setListError(
              error instanceof Error
                ? error.message
                : "Failed to load policies.",
            );
          }
        })
        .finally(() => {
          if (isCurrent()) setLoading(false);
        });
    },
    [
      autoSelectFirst,
      beginLoad,
      enabled,
      patchSearchParams,
      setCursor,
      setListError,
      setLoading,
      setRules,
      svcContext,
    ],
  );

  const loadMore = useCallback(() => {
    if (!enabled || !cursor || loadingMore) return;
    setLoadingMore(true);
    const query = usePolicyListStore.getState().search;
    void fetchPolicyList({ search: query, cursor, ctx: svcContext })
      .then((page) => {
        appendRules(page.rules);
        setCursor(page.hasMore ? page.nextCursor : null);
      })
      .finally(() => setLoadingMore(false));
  }, [
    appendRules,
    cursor,
    enabled,
    loadingMore,
    setCursor,
    setLoadingMore,
    svcContext,
  ]);

  useEffect(() => {
    if (!enabled || !syncEffects) return;
    const opening = policyOpenPending();
    if (opening) {
      // A search navigation that started before the click must not win.
      if (searchParams.get("policy") !== opening) {
        patchSearchParams((next) => {
          next.set("policy", opening);
          next.delete("q");
          next.delete("new");
        });
      }
      return;
    }
    const q = searchParams.get("q") ?? "";
    const current = usePolicyListStore.getState().search;
    const writeInFlight = searchSyncTimer != null || ignoreUrlQuery !== null;
    // Fast typing: an older `?q=` can commit after a newer one. It is a
    // prefix of the box (or of the query we just wrote). Leave the box.
    if (
      writeInFlight &&
      q !== current &&
      (current.startsWith(q) ||
        (ignoreUrlQuery != null && ignoreUrlQuery.startsWith(q)))
    ) {
      return;
    }
    if (ignoreUrlQuery !== null && q === ignoreUrlQuery) {
      // This echo is ours. The box may already be ahead of it.
      if (current === q || current.startsWith(q)) return;
    }
    ignoreUrlQuery = null;
    setSearch(q);
  }, [enabled, patchSearchParams, searchParams, setSearch, syncEffects]);

  useEffect(() => {
    if (!enabled || !syncEffects) return;
    if (firstLoad.current) {
      firstLoad.current = false;
      load(search);
      return;
    }
    armPolicySearchSync(() => {
      const trimmed = usePolicyListStore.getState().search.trim();
      const opening = policyOpenPending();
      if (opening) {
        const live =
          typeof window !== "undefined" ? window.location.search : "";
        if (new URLSearchParams(live).get("policy") !== opening) {
          patchSearchParams((next) => {
            next.set("policy", opening);
            next.delete("q");
            next.delete("new");
          });
        }
        load("");
        return;
      }
      ignoreUrlQuery = trimmed;
      patchSearchParams((next) => {
        if (trimmed) next.set("q", trimmed);
        else next.delete("q");
      });
      load(trimmed);
    });
  }, [enabled, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !syncEffects || typeof window === "undefined") return;
    const handler = () => {
      patchSearchParams((next) => {
        next.delete("policy");
        next.delete("thread");
        next.delete("draft");
        next.delete("new");
      });
      load(usePolicyListStore.getState().search);
    };
    window.addEventListener(ORG_TEAM_SELECTION_CHANGED_EVENT, handler);
    return () =>
      window.removeEventListener(ORG_TEAM_SELECTION_CHANGED_EVENT, handler);
  }, [enabled, load, patchSearchParams, syncEffects]);

  return {
    rules,
    search,
    setSearch,
    loading,
    listError,
    load,
    loadMore,
    loadingMore,
    hasMore: cursor != null,
    /** Open policy from `?policy=` — sidebar highlights this row. */
    selectedId: searchParams.get("policy"),
    /** Creating a new policy — do not highlight a previous row. */
    isCreating: searchParams.get("new") === "1",
  };
}
